import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import Navigation from './Navigation';
import Home from './Home';
import BuyerManagement from './BuyerManagement';
import PropertyManagement from './PropertyManagement';
import PropertyEvaluation from './PropertyEvaluation';
import PropertyDetail from "./PropertyDetail";
import AgentBoard from "./AgentBoard";
import BuyerCriteria from './BuyerCriteria';
import MatchBoard from './MatchBoard';
import UserManagement from './UserManagement';
import BrokerageManagement from './BrokerageManagement';
import BrokerDashboard from './BrokerDashboard';
import ChangePassword from './ChangePassword';
import Login from './Login';
import TrialLogin from './TrialLogin';
import Signup from './Signup';
import SignupSuccess from './SignupSuccess';
import BuyerSignup from './BuyerSignup';
import SuperAdminAssociations from './SuperAdminAssociations';
import AssociationConsole from './AssociationConsole';
import BrokerConsole from './BrokerConsole';
import AssociationTree from './AssociationTree';
import AiDARevenueReport from './AiDARevenueReport';
import SetPassword from './SetPassword';
import BrokerageSignup from './BrokerageSignup';
import SwitchLanding from './SwitchLanding';
import Reports from './Reports';
import ReportViewer from './ReportViewer';
import ChatWidget from './ChatWidget';
import ChatPage from './ChatPage';
import ListingLab from './ListingLab';
import ClientImport from './ClientImport';
import Sellers from './Sellers';

const ComingSoon = ({ title }) => (
  <div className="min-h-screen bg-[#0A0E27] pt-12">
    <div className="max-w-7xl mx-auto px-6">
      <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 text-center py-20">
        <h1 className="text-white text-3xl font-bold mb-4">{title}</h1>
        <p className="text-[#B0BEC5] text-lg">Coming Soon</p>
      </div>
    </div>
  </div>
);

function AppContent() {
  const { user, loading } = useAuth();
  const location = useLocation();

  // The cross-app switch landing must run BEFORE any session-based routing, so a
  // stale session already stored in THIS app can never short-circuit it (that is
  // exactly how a switch could previously drop you into the wrong account).
  if (location.pathname === '/switch') return <SwitchLanding />;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center">
        <div className="text-[#00D4FF] text-xl">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/trial-login" element={<TrialLogin />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/signup-success" element={<SignupSuccess />} />
        <Route path="/join/:code" element={<BuyerSignup />} />
        <Route path="/set-password" element={<SetPassword />} />
        <Route path="/signup-brokerage/:slug" element={<BrokerageSignup />} />
        <Route path="/switch" element={<SwitchLanding />} />
        <Route path="*" element={<Login />} />
      </Routes>
    );
  }

  if (user.role === 'association_admin') {
    return <AssociationConsole />;
  }

  // Brokerage admins live in the console (their only screen). Brokers land in the main app and
  // reach the console at /brokerage (full-screen); agents are full app users (their book).
  if (user.account_type === 'brokerage_user' && user.role === 'brokerage_admin') {
    return <BrokerConsole />;
  }
  if (user.account_type === 'brokerage_user' && user.role === 'broker' && location.pathname === '/brokerage') {
    return <BrokerConsole />;
  }

  return (
    <div className="min-h-screen bg-[#0A0E27]">
      <Navigation />
      <Routes>
        {/* Dashboard */}
        <Route path="/" element={<Home />} />

        {/* Buyer Management — agent+ */}
        <Route path="/buyers" element={<BuyerManagement />} />
        <Route path="/buyers/:buyerId/criteria" element={<BuyerCriteria />} />

        {/* Property Management — agent+ */}
        <Route path="/properties" element={<PropertyManagement />} />
        <Route path="/properties/:propertyId" element={<PropertyDetail />} />
        <Route path="/properties/:propertyId/buyer/:buyerId" element={<PropertyEvaluation />} />

        {/* Match Board — all roles */}
        <Route path="/agent-board" element={<AgentBoard />} />
        <Route path="/match-board" element={<MatchBoard />} />

        {/* Reports — agent/broker/super_admin (page + viewer self-gate buyers) */}
        <Route path="/reports" element={<Reports />} />
        <Route path="/reports/view" element={<ReportViewer />} />

        {/* AI Chat — full-page route (mobile + desktop); self-gates non-book roles */}
        <Route path="/chat" element={<ChatPage />} />

        {/* Listing Lab — agent/broker pre-listing analysis (self-gates non-book roles) */}
        <Route path="/listing-lab" element={<ListingLab />} />
        <Route path="/listing-lab/:propertyId" element={<ListingLab />} />

        {/* Client/seller import + seller records — agent/broker (self-gate non-book roles) */}
        <Route path="/import" element={<ClientImport />} />
        <Route path="/sellers" element={<Sellers />} />

        {/* Admin */}
        <Route path="/users" element={user.role === 'super_admin' ? <AssociationTree /> : <UserManagement />} />
        <Route path="/brokerages" element={<BrokerageManagement />} />
        <Route path="/associations" element={<SuperAdminAssociations />} />
        <Route path="/reports/aida-revenue" element={user.role === 'super_admin' ? <AiDARevenueReport /> : <Navigate to="/" replace />} />
        <Route path="/broker-dashboard" element={<BrokerDashboard />} />

        {/* Account */}
        <Route path="/change-password" element={<ChangePassword />} />

        {/* Signup (accessible when logged in for referrals) */}
        <Route path="/signup" element={<Signup />} />
        <Route path="/signup-success" element={<SignupSuccess />} />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Floating AI chat bubble — global on every page except the full-page /chat
          route. Self-gates: renders nothing for buyers or non-book roles. */}
      {location.pathname !== '/chat' && <ChatWidget />}
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
